import React from "react";
import { motion } from "framer-motion";
import ImageCarousel from "../../Components/ImageCarousel";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

export default function HeroSection({ onNavigate }) {
  const { t } = useLanguage();
  const heroTitle = t.hero.title;
  const subtitle = t.hero.subtitle;

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.5,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <ImageCarousel />

      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-black/40 z-[1]" />

      {/* Subtle geometric elements */}
      <div className="absolute inset-0 z-5">
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute top-20 right-20 w-32 h-32 border border-white/10 rounded-full"
        />
        <motion.div
          animate={{
            rotate: [360, 0],
            scale: [1.1, 1, 1.1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute bottom-32 left-16 w-24 h-24 border border-[--accent]/20 rounded-full"
        />
      </div>

      {/* Main content - Golden bordered box */}
      <motion.div
        variants={containerAnimation}
        initial="hidden"
        animate="visible"
        // Changed positioning for mobile to be lower: "top-[65%] md:top-1/2" instead of "top-1/2"
        className="absolute left-1/2 md:left-8 top-[65%] md:top-1/2 transform -translate-x-1/2 md:translate-x-0 -translate-y-1/2 z-10 text-center md:text-left max-w-xl mx-1 md:mx-0 p-6 md:p-10 w-full md:w-auto"
        style={{
          border: "none",
          background: "transparent",
          borderRadius: "1rem"
        }}
      >
        {/* Main title - Simplified */}
        <motion.h1
          variants={itemAnimation}
          // Changed flex direction to row for all screens, centred on mobile
          className="text-6xl md:text-8xl italic mb-6 md:mb-10 leading-tight tracking-wide flex flex-row items-center justify-center md:justify-start gap-4 md:gap-8"
          style={{
            fontFamily: "'Play', sans-serif",
            fontWeight: "bold",
            color: "#e8e8e8",
            textShadow: "0 0 20px rgba(212, 175, 55, 0.8), 0 0 40px rgba(212, 175, 55, 0.5)",
          }}
        >
          {/* Logo first as requested */}
          <motion.img
            src="./assets/EllegiLogoStartup.png"
            alt="ELLEGI Logo"
            className="h-20 md:h-32 w-auto filter drop-shadow-lg"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          />
          <span>{heroTitle}</span>
        </motion.h1>

        {/* Subtitle - Simplified */}
        <motion.p
          variants={itemAnimation}
          className="text-lg md:text-2xl text-gray-200 mb-8 md:mb-12 font-medium tracking-widest uppercase"
          style={{
            textShadow: "1px 1px 3px rgba(0, 0, 0, 0.8)",
          }}
        >
          {subtitle}
        </motion.p>

        {/* CTA Button - Filled with golden background */}
        <div className="flex justify-center md:justify-start">
          <motion.button
            variants={itemAnimation}
            onClick={() => onNavigate('about')}
            className="group relative bg-[--accent] text-black px-8 py-3 md:px-10 md:py-4 rounded-xl font-bold text-base md:text-xl transition-all duration-300 flex items-center shadow-lg hover:shadow-xl"
            style={{
              boxShadow: "0 0 20px rgba(212, 175, 55, 0.4)",
            }}
            whileHover={{
              scale: 1.05,
              backgroundColor: "#E4C04A", // darker gold/yellow
              boxShadow: "0 0 30px rgba(212, 175, 55, 0.6)",
            }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10 flex items-center">
              {t.hero.cta}
              <motion.div
                className="ml-3"
                animate={{ x: [0, 5, 0] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <ArrowRight className="w-6 h-6" />
              </motion.div>
            </span>
          </motion.button>
        </div>

        {/* Scroll indicator */}
        <motion.div
          variants={itemAnimation}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center text-white/60"
          >
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Gradient Footer */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
    </section >
  );
}
